/**
 * App chrome: navigation rail + top bar + page outlet.
 *
 * Investigator navigation, support, announcements, and page tutorials.
 */
import React from "react";
import { Box, Toolbar } from "@mui/material";
import { SideBar } from "./SideBar";
import TopAppBar from "./TopAppBar";
import { ToastOffsetSync } from "../../shared/toast";
import { ReportIssueWidget } from "../feedback/ReportIssueWidget";
import { useAuth } from "@workos-inc/authkit-react";
import { useIncidentManagementEnabled } from "@/pages/IncidentManagement/featureFlag";
import { useNewsModal } from "@/shared/hooks/useNewsModal";
import { NewsModal } from "@/shared/ui/modals/NewsModal";
import { PageTourProvider } from "@/features/pageTour/PageTourProvider";

export const MainComponent: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const investigatorEnabled = useIncidentManagementEnabled();
  const {
    newsItems,
    open,
    present,
    canOpen,
    handleOpen,
    handleClose,
    handleExited,
  } = useNewsModal(user?.id, investigatorEnabled);

  return (
    <PageTourProvider userId={user?.id} paused={present}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <TopAppBar />
        {/*
          The AppBar is position="fixed" -- deliberately, because it spans the full
          width so the logo sits above the navigation rail. Fixed means it is out of
          flow and contributes NO height, so anything laid out after it starts at
          y=0 and slides underneath.
          This spacer reserves that height. A plain Toolbar is exactly as tall as
          the AppBar's own Toolbar at every breakpoint, including the dense
          variants, which a hardcoded pixel offset is not -- that is what ReportGen
          used (appBarHeightDesktop/appBarHeightMobile) and what silently drifts
          when the bar's contents change.
        */}
        <Toolbar />
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <SideBar onOpenNews={canOpen ? handleOpen : undefined} />
          <Box
            component="main"
            sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, overflow: "auto" }}
          >
            {children}
          </Box>
        </Box>
        <ToastOffsetSync />
        <ReportIssueWidget />
        <NewsModal
          open={open}
          onClose={handleClose}
          onExited={handleExited}
          newsItems={newsItems}
        />
      </Box>
    </PageTourProvider>
  );
};

export default MainComponent;
